import { useEffect, useRef, useState } from "react";
import { Hammer } from "lucide-react";
import ScoreSubmit from "@/components/landing/games/ScoreSubmit";

const HOLES = 9;
const DURATION = 20;

export default function WhackANail() {
  const [active, setActive] = useState(null);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(DURATION);
  const [running, setRunning] = useState(false);
  const timers = useRef([]);

  useEffect(() => {
    if (!running) return;
    const pop = setInterval(() => setActive(Math.floor(Math.random() * HOLES)), 650);
    const clock = setInterval(() => setTimeLeft((t) => t - 1), 1000);
    timers.current = [pop, clock];
    return () => timers.current.forEach(clearInterval);
  }, [running]);

  useEffect(() => {
    if (timeLeft <= 0 && running) {
      setRunning(false);
      setActive(null);
    }
  }, [timeLeft, running]);

  const start = () => {
    setScore(0);
    setTimeLeft(DURATION);
    setRunning(true);
  };

  const whack = (i) => {
    if (!running || i !== active) return;
    setScore((s) => s + 1);
    setActive(null);
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E6DCD3] p-6 sm:p-8 shadow-sm" data-testid="whack-a-nail-game">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-bold text-base text-[#1E252B]">Whack-a-Nail</h3>
          <p className="text-xs text-[#736B63] mt-0.5">Hammer every nail that pops up before the timer runs out.</p>
        </div>
        <span className="text-xs font-bold text-[#D4691E]" data-testid="nail-score">
          {running ? `${timeLeft}s left • ${score} hammered` : `${score} hammered`}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-3 max-w-sm mx-auto">
        {Array.from({ length: HOLES }).map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => whack(i)}
            data-testid={`nail-hole-${i}`}
            className="aspect-square rounded-xl bg-[#F5EFEB] border border-[#E6DCD3] flex items-center justify-center transition-colors hover:bg-[#EFE9E1]"
          >
            {i === active && (
              <span className="w-3 h-10 rounded-t-full bg-[#8C847B] border-2 border-[#544D45] shadow-sm animate-bounce" />
            )}
          </button>
        ))}
      </div>

      <div className="text-center mt-5">
        {!running && (
          <>
            {timeLeft <= 0 && (
              <p className="text-sm font-semibold text-[#1E252B] mb-3" data-testid="nail-result">
                {score >= 12
                  ? "Carpenter-grade reflexes. Your framing would be dead level."
                  : "Not bad — but I never miss a nail on your job."}
              </p>
            )}
            {timeLeft <= 0 && score > 0 && <ScoreSubmit game="whack-a-nail" score={score} scoreLabel="nails" />}
            <button
              type="button"
              onClick={start}
              data-testid="nail-start-btn"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[#D4691E] hover:bg-[#A94E10] text-white text-sm font-bold transition-colors"
            >
              <Hammer className="w-4 h-4" /> {timeLeft <= 0 ? "Play Again" : "Start Hammering"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
